// Shared page-system primitives (TimeKeep Page System spec, build step 2).
//
// Every color here is read from C inside a render or a function call, never
// at module scope: applyTheme() mutates C after this module has loaded, so a
// module-level object holding C.xxx would freeze the pre-theme colors (the
// STATUS_CFG / ACTION_BADGE bug class). Module-level data may hold tone NAMES
// ('danger', 'good'); toneColor() turns a name into a color at call time.
//
// C, css, contrastText and TourButton come back out of Dashboard.js -- the same
// circular import DashboardShareModal.js and ContractsView.js already use.
// Nothing in this file touches those bindings at module-evaluation time.

import { useState, useEffect, useRef, useMemo } from 'react';
import TKTooltip from './TKTooltip';
import { C, contrastText, TourButton } from './Dashboard';

const FONT = 'Inter,sans-serif';

// ─── Tones ────────────────────────────────────────────────────────────────────
// Semantic tones only. The accent is for interaction (selected controls, links,
// primary buttons), never for what a number means -- an org can pick any accent.
export const toneColor = (tone) => ({
  danger:  C.danger,
  good:    C.success,
  warn:    C.badgeAmber,
  info:    C.badgeBlue,
  neutral: C.badgeGray,
  purple:  C.badgePurple,
})[tone] || C.muted;

// ─── TabBar · page-level sections ──────────────────────────────────────────────
// Moved from Dashboard.js. The track used rgba(0,0,0,0.15), which disappears on
// dark themes; it now uses C.bg with a C.border outline. It scrolls sideways when
// narrow instead of wrapping (callers can still pass style to override).
export function TabBar({ tabs, active, onChange, style }) {
  return (
    <div role="tablist" style={{display:'inline-flex',gap:'2px',background:C.bg,border:`1px solid ${C.border}`,
      borderRadius:'9px',padding:'3px',maxWidth:'100%',overflowX:'auto',...style}}>
      {tabs.map(([k,lb]) => (
        <button key={k} type="button" role="tab" aria-selected={active===k} onClick={()=>onChange(k)} style={{
          padding:'6px 18px',fontSize:'13px',fontWeight:active===k?'700':'400',
          border:'none',cursor:'pointer',borderRadius:'7px',whiteSpace:'nowrap',
          fontFamily:FONT,transition:'all 0.15s',
          background: active===k ? C.surface : 'transparent',
          color: active===k ? C.accent : C.textDim,
          boxShadow: active===k ? '0 1px 3px rgba(0,0,0,0.25)' : 'none',
        }}>{lb}</button>
      ))}
    </div>
  );
}

// ─── SegmentedControl · range and view toggles ──────────────────────────────────
// Moved from Dashboard.js. Selected text was a hardcoded #fff, unreadable on a
// light accent (e.g. the Forest preset's #4ade80); it now uses contrastText.
export function SegmentedControl({ options, value, onChange }) {
  const onAccent = contrastText(C.accent);
  return (
    <div role="group" style={{display:'inline-flex',alignItems:'center',alignSelf:'flex-start',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'3px',gap:'2px',maxWidth:'100%',flexWrap:'wrap'}}>
      {options.map(([k,lb]) => (
        <button key={k} type="button" aria-pressed={value===k} onClick={()=>onChange(k)} style={{
          padding:'5px 12px',fontSize:'12px',fontWeight:'600',border:'none',cursor:'pointer',
          borderRadius:'6px',fontFamily:FONT,transition:'all 0.15s',
          background: value===k ? C.accent : 'transparent',
          color: value===k ? onAccent : C.textDim,
        }}>{lb}</button>
      ))}
    </div>
  );
}

// ─── Page · the slot-based layout shell ─────────────────────────────────────────
// Slots, in fixed order:
//   banner    page-level notice above the title (View As bar, submit nudge)
//   A title   title + tour "?" + secondary actions + at most ONE primary action
//   B tabs    one TabBar ({ tabs, active, onChange }) -- one level per surface
//   C controls a <Controls> (status pills line + search/filters/range line)
//   D stats   a <StatTiles> or its items array (max 4)
//   E children
// `primary` is a single node on purpose: the "one filled button per page" rule is
// enforced by the shape of the API, not by review.
// `tour` maps slot names to WalkthroughModal anchors: { header, tabs, controls, stats }
// become data-tour attributes on those slots, so tours keep pointing at them.
export function Page({ title, subtitle, onTour, actions, primary, banner, tabs, controls, stats, children, tour = {} }) {
  return (
    <div style={{padding:'28px',paddingBottom:'72px',display:'flex',flexDirection:'column',gap:'14px',minWidth:0}}>
      {banner}
      <div data-tour={tour.header} style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
        <div style={{minWidth:0}}>
          <h1 style={{margin:0,fontSize:'20px',fontWeight:'700',color:C.textBright,fontFamily:FONT}}>{title}</h1>
          {subtitle && <div style={{fontSize:'12px',color:C.muted,marginTop:'2px'}}>{subtitle}</div>}
        </div>
        {onTour && <TourButton onClick={onTour} />}
        <div style={{flex:1}} />
        {(actions || primary) && (
          <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
            {actions}
            {primary}
          </div>
        )}
      </div>
      {tabs && <div data-tour={tour.tabs} style={{alignSelf:'flex-start',maxWidth:'100%'}}><TabBar tabs={tabs.tabs} active={tabs.active} onChange={tabs.onChange} /></div>}
      {controls && <div data-tour={tour.controls}>{controls}</div>}
      {stats && <div data-tour={tour.stats}>{Array.isArray(stats) ? <StatTiles items={stats} /> : stats}</div>}
      {children}
    </div>
  );
}

// Controls slot: line 1 = status pills; line 2 = search -> filters, then the
// date range right-aligned. Every control here also scopes the stat tiles.
export function Controls({ pills, search, filters, range }) {
  const hasLine2 = search || filters || range;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
      {pills}
      {hasLine2 && (
        <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
          {search}
          {filters}
          <div style={{flex:1}} />
          {range && <div style={{display:'flex',alignItems:'center',gap:'8px'}}>{range}</div>}
        </div>
      )}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder='Search…', width='240px', id }) {
  return (
    <input id={id} type="search" value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'6px',color:C.text,padding:'7px 10px',
        fontSize:'13px',width,maxWidth:'100%',outline:'none',boxSizing:'border-box',fontFamily:FONT}} />
  );
}

// ─── StatTiles · one row, max four ──────────────────────────────────────────────
// item: { label, value, basis, tip, tone, provisional, onClick }
//   basis        muted line under the value saying what's in it (node or string)
//   tone         semantic tone for the VALUE ('danger' for Overdue); default neutral
//   provisional  string -> amber chip ("1 of 3 member invoices approved")
//   onClick      makes the tile a link to where that number is managed
export function StatTiles({ items = [] }) {
  if (items.length > 4 && process.env.NODE_ENV !== 'production') {
    console.warn('[StatTiles] max four tiles per page; extra tiles dropped:', items.slice(4).map(i => i.label));
  }
  const shown = items.slice(0, 4);
  return (
    <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fit,minmax(170px,1fr))`,gap:'10px'}}>
      {shown.map((it, i) => <StatTile key={it.key || it.label || i} {...it} />)}
    </div>
  );
}

export function StatTile({ label, value, basis, tip, tone, provisional, onClick, title }) {
  const amber = C.badgeAmber;
  const labelNode = tip ? <TKTooltip text={tip}>{label} ⓘ</TKTooltip> : label;
  return (
    <div role={onClick ? 'link' : undefined} tabIndex={onClick ? 0 : undefined} title={title}
      onClick={onClick} onKeyDown={onClick ? (e => { if (e.key === 'Enter') onClick(); }) : undefined}
      style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'14px 16px',
      display:'flex',flexDirection:'column',gap:'2px',minWidth:0,cursor:onClick?'pointer':'default'}}>
      <div style={{fontSize:'11px',fontWeight:'600',letterSpacing:'0.05em',textTransform:'uppercase',color:C.muted}}>{labelNode}</div>
      <div style={{fontSize:'24px',fontWeight:'700',color: tone ? toneColor(tone) : C.textBright,
        fontVariantNumeric:'tabular-nums',lineHeight:1.25}}>{value}</div>
      {basis != null && basis !== '' && <div style={{fontSize:'12px',color:C.muted}}>{basis}</div>}
      {provisional && (
        <span style={{alignSelf:'flex-start',marginTop:'4px',fontSize:'11px',fontWeight:'600',borderRadius:'4px',
          padding:'2px 6px',background:`${amber}26`,color:amber}}>Provisional · {provisional}</span>
      )}
    </div>
  );
}

// ─── StatusPills · record-status filters ────────────────────────────────────────
// option: { key, label, count, total, tone }
//   total  pre-formatted (string/node) -- formatting is the caller's, until format.js
//   tone   'danger' for problem statuses (Overdue, Disputed, Rejected)
// Order is the caller's: the status needing action first (the default), All last.
export function StatusPills({ options, value, onChange }) {
  return (
    <div role="group" style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
      {options.map(o => {
        const on = value === o.key;
        const fill = o.tone ? toneColor(o.tone) : C.accent;
        const onFill = contrastText(fill);
        const idleText = o.tone && o.count > 0 ? toneColor(o.tone) : C.text;
        return (
          <button key={o.key} type="button" aria-pressed={on} onClick={()=>onChange(o.key)} style={{
            display:'inline-flex',alignItems:'baseline',gap:'6px',padding:'6px 12px',borderRadius:'999px',
            fontSize:'12px',fontWeight:'600',cursor:'pointer',fontFamily:FONT,whiteSpace:'nowrap',
            border:`1px solid ${on ? fill : C.border}`,
            background: on ? fill : C.surface,
            color: on ? onFill : idleText,
          }}>
            {o.label}
            {(o.count != null || o.total != null) && (
              <span style={{fontSize:'11px',fontWeight:'500',color: on ? onFill : C.muted,opacity: on ? 0.85 : 1,fontVariantNumeric:'tabular-nums'}}>
                {o.count != null ? o.count : ''}{o.count != null && o.total != null ? ' · ' : ''}{o.total != null ? o.total : ''}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── StatusBadge ────────────────────────────────────────────────────────────────
// Tone + label only. A per-record-type status registry (labels, tones) is part of
// the status-pill work that depends on the spec's pending decisions, so callers
// pass the label and tone explicitly for now.
export function StatusBadge({ tone = 'neutral', children, title }) {
  const c = toneColor(tone);
  return (
    <span title={title} style={{display:'inline-block',padding:'2px 8px',borderRadius:'4px',fontSize:'11px',
      fontWeight:'600',whiteSpace:'nowrap',color:c,background:`${c}22`}}>{children}</span>
  );
}

// ─── EmptyState ─────────────────────────────────────────────────────────────────
// Two kinds: "nothing yet" (action = the page's primary action) and "no matches"
// (action = clear filters). Keep the body to one sentence.
export function EmptyState({ title, body, action }) {
  return (
    <div style={{padding:'36px 20px',textAlign:'center',display:'flex',flexDirection:'column',alignItems:'center',gap:'6px'}}>
      <div style={{color:C.textBright,fontSize:'14px',fontWeight:'600'}}>{title}</div>
      {body && <div style={{color:C.muted,fontSize:'13px',maxWidth:'44ch'}}>{body}</div>}
      {action && <div style={{marginTop:'8px'}}>{action}</div>}
    </div>
  );
}

function SkeletonRows({ cols, rows = 5 }) {
  return Array.from({length: rows}).map((_, r) => (
    <tr key={r}>
      {Array.from({length: cols}).map((__, c) => (
        <td key={c} style={{padding:'12px',borderBottom:`1px solid ${C.border}`}}>
          <div style={{height:'10px',borderRadius:'4px',background:C.border,opacity:0.6,width:`${55 + ((r*7 + c*13) % 40)}%`}} />
        </td>
      ))}
    </tr>
  ));
}

// ─── Row actions "⋯" menu ───────────────────────────────────────────────────────
export function RowMenu({ actions }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  // Fixed positioning so the menu isn't clipped by the table's scroll container;
  // opens upward when there's no room below.
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      const below = window.innerHeight - r.bottom;
      setPos({ right: Math.max(4, window.innerWidth - r.right),
               ...(below < 160 ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    }
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    const shut = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', shut, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); window.removeEventListener('scroll', shut, true); };
  }, [open]);
  if (!actions || actions.length === 0) return null;
  return (
    <div ref={ref} style={{position:'relative',display:'inline-block'}} onClick={e=>e.stopPropagation()}>
      <button type="button" aria-label="Row actions" aria-haspopup="menu" aria-expanded={open} onClick={toggle}
        style={{background:'transparent',border:'none',cursor:'pointer',color:C.muted,fontSize:'16px',lineHeight:1,padding:'4px 8px',borderRadius:'4px'}}>⋯</button>
      {open && (
        <div role="menu" style={{position:'fixed',...pos,zIndex:750,minWidth:'170px',background:C.surface,
          border:`1px solid ${C.border}`,borderRadius:'8px',boxShadow:'0 8px 24px rgba(0,0,0,0.25)',padding:'4px',textAlign:'left'}}>
          {actions.map((a, i) => (
            <button key={i} type="button" role="menuitem" disabled={a.disabled}
              onClick={()=>{ setOpen(false); a.onClick && a.onClick(); }}
              style={{display:'block',width:'100%',textAlign:'left',background:'transparent',border:'none',
                cursor:a.disabled?'default':'pointer',padding:'8px 10px',borderRadius:'6px',fontSize:'13px',fontFamily:FONT,
                color: a.danger ? C.danger : C.text, opacity: a.disabled ? 0.5 : 1}}>{a.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DataTable ──────────────────────────────────────────────────────────────────
// column: { key, label, align, width, render(row), sortValue(row), sortable, tip }
// Sorting is controlled when `sort` + `onSort` are passed, otherwise internal.
// Rules (spec): first column is the record's name/number; money right-aligned
// and C.textBright (never the accent); no buttons in rows -- row actions go in
// rowActions(row) -> [{ label, onClick, danger, disabled }], the main action in
// the DetailPanel that onRowClick opens.
export function DataTable({ columns, rows, rowKey = r => r.id, onRowClick, selectedKey, sort, onSort,
                            rowActions, loading, empty }) {
  const [innerSort, setInnerSort] = useState(null);
  const activeSort = onSort ? sort : innerSort;
  const sorted = useMemo(() => {
    if (onSort || !activeSort) return rows;
    const col = columns.find(c => c.key === activeSort.key);
    if (!col) return rows;
    const get = col.sortValue || (r => r[col.key]);
    const dir = activeSort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * dir;
    });
  }, [rows, columns, activeSort, onSort]);

  const clickSort = (col) => {
    if (!(col.sortable || col.sortValue)) return;
    const next = activeSort?.key === col.key && activeSort.dir === 'asc' ? { key: col.key, dir: 'desc' } : { key: col.key, dir: 'asc' };
    onSort ? onSort(next) : setInnerSort(next);
  };

  const colCount = columns.length + (rowActions ? 1 : 0);
  const th = { textAlign:'left', padding:'9px 12px', color:C.muted, fontWeight:'600', fontSize:'11px', letterSpacing:'0.04em',
    textTransform:'uppercase', borderBottom:`1px solid ${C.border}`, background:C.surface, position:'sticky', top:0, zIndex:1, whiteSpace:'nowrap' };
  const td = { padding:'10px 12px', borderBottom:`1px solid ${C.border}`, color:C.textDim, fontSize:'13px', height:'40px' };

  return (
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'10px',overflow:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontVariantNumeric:'tabular-nums'}}>
        <thead>
          <tr>
            {columns.map(col => {
              const sortable = col.sortable || col.sortValue;
              const arrow = activeSort?.key === col.key ? (activeSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
              const label = col.tip ? <TKTooltip text={col.tip}>{col.label} ⓘ</TKTooltip> : col.label;
              return (
                <th key={col.key} scope="col" aria-sort={activeSort?.key===col.key ? (activeSort.dir==='asc'?'ascending':'descending') : undefined}
                  style={{...th, textAlign:col.align||'left', width:col.width, cursor:sortable?'pointer':'default'}}
                  onClick={()=>clickSort(col)}>{label}{arrow}</th>
              );
            })}
            {rowActions && <th style={{...th, width:'40px'}} aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {loading ? <SkeletonRows cols={colCount} /> : sorted.length === 0 ? (
            <tr><td colSpan={colCount} style={{padding:0}}>{empty || <EmptyState title="Nothing here yet" />}</td></tr>
          ) : sorted.map(row => {
            const k = rowKey(row);
            const selected = selectedKey != null && selectedKey === k;
            return (
              <tr key={k}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e => { if (e.key === 'Enter') onRowClick(row); }) : undefined}
                style={{cursor:onRowClick?'pointer':'default', background: selected ? `${C.accent}14` : 'transparent'}}>
                {columns.map((col, ci) => (
                  <td key={col.key} style={{...td, textAlign:col.align||'left',
                    ...(ci === 0 ? {color:C.textBright,fontWeight:'600'} : {})}}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
                {rowActions && <td style={{...td, textAlign:'center', width:'40px'}}><RowMenu actions={rowActions(row)} /></td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── DetailPanel ────────────────────────────────────────────────────────────────
// Right-side panel over the page, like ClientDetailDrawer. 520px wide, full width
// on narrow screens. Esc or the scrim closes it. `primary` is the record's one
// main action; `actions` are secondary. A panel is its own surface, so it may
// carry its own TabBar in `children`.
export function DetailPanel({ open, onClose, title, badges, subtitle, primary, actions, children, width = 520 }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div style={{position:'fixed',inset:0,zIndex:700,display:'flex',justifyContent:'flex-end'}}>
      <div onClick={onClose} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.35)'}} />
      <aside role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}
        style={{position:'relative',width:`min(${width}px, 100vw)`,height:'100%',overflowY:'auto',background:C.surface,
          borderLeft:`1px solid ${C.border}`,boxShadow:'-8px 0 32px rgba(0,0,0,0.3)',display:'flex',flexDirection:'column',fontFamily:FONT}}>
        <div style={{padding:'20px 22px 14px',borderBottom:`1px solid ${C.border}`,display:'flex',flexDirection:'column',gap:'8px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
            <h2 style={{margin:0,fontSize:'18px',fontWeight:'700',color:C.textBright}}>{title}</h2>
            {badges}
            <div style={{flex:1}} />
            <button type="button" aria-label="Close" onClick={onClose}
              style={{background:'transparent',border:`1px solid ${C.border}`,borderRadius:'6px',color:C.muted,cursor:'pointer',padding:'4px 10px',fontSize:'13px'}}>✕</button>
          </div>
          {subtitle && <div style={{fontSize:'13px',color:C.muted}}>{subtitle}</div>}
          {(primary || actions) && <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>{primary}{actions}</div>}
        </div>
        <div style={{padding:'18px 22px',display:'flex',flexDirection:'column',gap:'14px'}}>{children}</div>
      </aside>
    </div>
  );
}
